import React, { useState } from 'react';
import {
  CheckCircle2,
  Lightbulb,
  Rocket,
  Cloud,
  TrendingUp,
  Code2,
  Link2,
  Headphones,
  BarChart3,
  GraduationCap,
  Sparkles,
} from 'lucide-react';

const services = [
  {
    title: 'Consulting',
    heading: 'Infor M3 Consulting',
    icon: Lightbulb,
    description:
      'We provide strategic consulting to help you maximize your Infor M3 investment. Our team conducts comprehensive business process assessments to identify improvement opportunities, develops ERP strategies aligned with your business goals, creates detailed solution architectures, and delivers industry best-practice guidance. We work closely with your leadership to define clear roadmaps that ensure your M3 system supports your operational objectives while maintaining flexibility for future growth.',
    points: [
      'Business process assessment',
      'ERP strategy and roadmap',
      'Solution architecture and design',
    ],
  },
  {
    title: 'Implementation',
    heading: 'Infor M3 Implementation',
    icon: Rocket,
    description:
      'We manage end-to-end Infor M3 implementations from project kickoff to go-live and beyond. Our implementation methodology covers business process configuration, module deployment, project management, and comprehensive go-live support. We ensure that every functional area—from supply chain to finance to manufacturing—is properly configured to support your unique business processes. Our team works alongside your project team to establish realistic timelines, manage risks proactively, and deliver a system that drives operational excellence from day one.',
    points: [
      'End-to-end ERP implementation',
      'Business process configuration',
      'Project management and go-live support',
    ],
  },
  {
    title: 'Cloud Migration',
    heading: 'Infor M3 Cloud Migration',
    icon: Cloud,
    description:
      'We help organizations transition from on-premise Infor M3 to Infor CloudSuite with minimal disruption. Our migration approach covers data migration and validation, cloud optimization, and performance tuning to ensure your system runs efficiently in the cloud environment. We conduct thorough pre-migration assessments to identify potential challenges, develop comprehensive migration strategies, and provide post-migration support to ensure a smooth transition. Our expertise ensures you unlock the full benefits of cloud capabilities while maintaining system integrity and performance.',
    points: [
      'Migration from on-premise to Infor CloudSuite',
      'Data migration and validation',
      'Cloud optimization and performance tuning',
    ],
  },
  {
    title: 'Upgrades',
    heading: 'Infor M3 Upgrades',
    icon: TrendingUp,
    description:
      'We manage Infor M3 version upgrades with structured planning, testing, and deployment to ensure seamless transitions. Our upgrade services include feature enhancements, system modernization, and comprehensive upgrade testing to validate functionality before go-live. We review customizations, integrations, and data dependencies to identify potential issues early and develop mitigation strategies. Our team ensures your upgrade delivers new capabilities and improved performance while minimizing business disruption and maintaining system stability.',
    points: [
      'Version upgrades and feature enhancements',
      'System modernization',
      'Upgrade testing and deployment',
    ],
  },
  {
    title: 'Customization',
    heading: 'Customization & Development',
    icon: Code2,
    description:
      'We tailor Infor M3 to match your specific business requirements through custom workflows, personalization, API development, and extension development using Infor technologies. Our development approach ensures that customizations are built with future-proof architecture, minimal upgrade complexity, and optimal performance. We evaluate each customization for business value, security implications, and long-term maintainability. Whether you need specialized processes, unique interfaces, or enhanced functionality, we design solutions that extend M3 capabilities while protecting your core system integrity.',
    points: [
      'Custom workflows and personalization',
      'API and extension development',
      'Infor technologies implementation',
    ],
  },
  {
    title: 'Integration',
    heading: 'Infor M3 Integration',
    icon: Link2,
    description:
      'We connect Infor M3 with surrounding applications and systems to create a unified technology ecosystem. Our integration services include Infor OS integration, API integration, third-party application integration, EDI integration, and connections with CRM, WMS, MES, and eCommerce platforms. We design robust integration architectures that ensure reliable data flow, handle exceptions effectively, and provide real-time synchronization where needed. Our approach reduces manual rework, eliminates data silos, and creates a seamless digital experience across your enterprise.',
    points: [
      'Infor OS and API integration',
      'Third-party application integration',
      'EDI and eCommerce integration',
    ],
  },
  {
    title: 'Support',
    heading: 'Support & Managed Services',
    icon: Headphones,
    description:
      'We provide comprehensive 24×7 application support for Infor M3 including incident management, performance monitoring, preventive maintenance, and SLA-based support. Our support model includes tiered escalation, proactive monitoring, and regular system health checks to keep your operations running smoothly. We work collaboratively with your internal teams to build knowledge and reduce dependency over time, while ensuring your system remains optimized and aligned with evolving business requirements. Regular support reviews help prioritize improvements for maximum business impact.',
    points: [
      '24×7 application support',
      'Incident management and monitoring',
      'Preventive maintenance and SLA-based support',
    ],
  },
  {
    title: 'Optimization',
    heading: 'Business Process Optimization',
    icon: BarChart3,
    description:
      'We analyze and optimize your business processes to improve efficiency, visibility, and control within Infor M3. Our optimization services cover supply chain optimization, manufacturing process improvement, inventory optimization, warehouse optimization, and procurement optimization. We use data analysis and process mapping to identify bottlenecks, eliminate waste, and implement best-practice workflows. Our recommendations are grounded in real-world experience and designed to deliver measurable improvements in operational performance and cost reduction.',
    points: [
      'Supply chain and manufacturing optimization',
      'Inventory and warehouse optimization',
      'Procurement optimization',
    ],
  },
  {
    title: 'Training',
    heading: 'Training & User Enablement',
    icon: GraduationCap,
    description:
      'We prepare your teams for success with Infor M3 through comprehensive training and enablement programs. Our training services include end-user training, administrator training, technical training, user documentation, and knowledge transfer. We design role-based training that focuses on practical scenarios, approvals, exceptions, and handoffs so users understand both the system and the processes behind it. Our change management approach ensures teams are truly ready to embrace new workflows, driving adoption and accelerating time-to-value for your Infor M3 investment.',
    points: [
      'End-user and administrator training',
      'Technical training and user documentation',
      'Knowledge transfer and adoption support',
    ],
  },
];

const M3Overview = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeService = services[activeIndex];
  const ActiveIcon = activeService.icon;

  return (
    <section
      id="m3-overview"
      className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16"
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="mx-auto max-w-7xl">
        {/* Compact Header */}
        <div className="mb-10 flex flex-col items-center justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1"
              style={{ backgroundColor: 'rgba(255, 74, 61, 0.08)' }}>
              <Sparkles className="h-3 w-3" style={{ color: '#D81820' }} />
              <span
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: '#D81820' }}
              >
                M3 Services
              </span>
            </div>
            <h2
              className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl"
              style={{ color: '#1A2634' }}
            >
              Our Infor M3 Consulting Services
            </h2>
            <p
              className="mt-2 text-sm sm:text-base"
              style={{ color: '#000000' }}
            >
              Expert solutions across the entire ERP lifecycle.
            </p>
          </div>
          <div
            className="hidden sm:flex items-center gap-2 text-xs"
            style={{ color: '#9CA3AF' }}
          >
            <span className="font-semibold" style={{ color: '#D81820' }}>
              {String(activeIndex + 1).padStart(2, '0')}
            </span>
            <span>/</span>
            <span>{String(services.length).padStart(2, '0')}</span>
          </div>
        </div>

        {/* Tab Navigation with Full Buttons */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {services.map((service, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={service.title}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className="px-5 py-2.5 text-md font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
                  style={{
                    backgroundColor: isActive ? '#D81820' : 'transparent',
                    color: isActive ? '#FFFFFF' : '#000000',
                    boxShadow: isActive ? '0 4px 12px rgba(255, 0, 0, 0.3)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {service.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* Service Detail Card */}
        <div
          className="relative overflow-hidden rounded-2xl border p-6 sm:p-8 lg:p-10"
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.06)',
          }}
        >
          {/* Decorative elements */}
          <div
            className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full opacity-30 blur-3xl"
            style={{
              background:
                'radial-gradient(circle, rgba(255,74,61,0.2) 0%, transparent 70%)',
            }}
          />
          <div
            className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full opacity-20 blur-3xl"
            style={{
              background:
                'radial-gradient(circle, rgba(26,38,52,0.2) 0%, transparent 70%)',
            }}
          />

          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:gap-10 lg:items-center">
            {/* Left: Content */}
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl shrink-0"
                  style={{
                    backgroundColor: '#D81820',
                    color: '#f5f5f5',
                  }}
                >
                  <ActiveIcon className="h-6 w-6" />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold tracking-tight sm:text-2xl"
                    style={{ color: '#1A2634' }}
                  >
                    {activeService.heading}
                  </h3>
                </div>
              </div>

              <p
                className="text-sm leading-relaxed sm:text-base md:text-lg font-medium"
                style={{
                  color: '#000000',
                  textAlign: 'justify',
                  textJustify: 'inter-word',
                }}
              >
                {activeService.description}
              </p>
            </div>

          </div>

          {/* Bottom Progress Bar */}
          <div className="relative mt-8 flex items-center gap-1.5">
            {services.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className="h-1 rounded-full transition-all duration-300"
                style={{
                  width: idx === activeIndex ? '32px' : '8px',
                  backgroundColor:
                    idx === activeIndex ? '#D81820' : '#E5E7EB',
                }}
                aria-label={`Go to service ${idx + 1}`}
              />
            ))}
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() =>
                  setActiveIndex(
                    (prev) => (prev - 1 + services.length) % services.length
                  )
                }
                className="flex h-9 w-9 items-center justify-center rounded-full transition-all hover:scale-105"
                style={{
                  backgroundColor: '#F3F4F6',
                  color: '#1A2634',
                }}
                aria-label="Previous service"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() =>
                  setActiveIndex((prev) => (prev + 1) % services.length)
                }
                className="flex h-9 w-9 items-center justify-center rounded-full transition-all hover:scale-105"
                style={{
                  backgroundColor: '#D81820',
                  color: '#FFFFFF',
                }}
                aria-label="Next service"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default M3Overview;
